export interface BrowserFrameRecoveryState {
  readonly acceptedSequence: number;
  readonly observedSequence: number;
  readonly streamId?: string;
  readonly streamGeneration?: number;
  readonly acceptedCapturedAt?: string;
  readonly gapCount: number;
}

export interface BrowserFrameRecoveryInput {
  readonly sequence: number;
  readonly streamId?: string;
  readonly streamGeneration?: number;
  readonly keyframe?: boolean;
  readonly capturedAt: string;
}

export interface BrowserFrameRecoveryResult {
  readonly accept: boolean;
  readonly observe: boolean;
  readonly missingFrameCount: number;
  readonly state: BrowserFrameRecoveryState;
}

export const EMPTY_BROWSER_FRAME_RECOVERY_STATE = {
  acceptedSequence: 0,
  observedSequence: 0,
  gapCount: 0,
} satisfies BrowserFrameRecoveryState;

interface BrowserFrameDecision {
  readonly accept: boolean;
  readonly observe: boolean;
  readonly missingFrameCount: number;
  readonly startsNewStream: boolean;
}

const REJECT_BROWSER_FRAME = {
  accept: false,
  observe: false,
  missingFrameCount: 0,
  startsNewStream: false,
} satisfies BrowserFrameDecision;

function keyframeDecision(keyframe: boolean | undefined): BrowserFrameDecision {
  const accept = keyframe === true;
  return { accept, observe: accept, missingFrameCount: 0, startsNewStream: accept };
}

function initialVersionedFrameDecision(
  previous: BrowserFrameRecoveryState,
  next: BrowserFrameRecoveryInput,
): BrowserFrameDecision | null {
  const hasNoVersionedStream =
    previous.streamGeneration === undefined && previous.streamId === undefined;
  const startsVersionedStream = next.streamGeneration !== undefined || next.streamId !== undefined;
  return hasNoVersionedStream && startsVersionedStream ? keyframeDecision(next.keyframe) : null;
}

function frameGenerationDecision(
  previous: BrowserFrameRecoveryState,
  next: BrowserFrameRecoveryInput,
): BrowserFrameDecision | null {
  if (previous.streamGeneration === undefined || next.streamGeneration === undefined) return null;
  if (next.streamGeneration < previous.streamGeneration) return REJECT_BROWSER_FRAME;
  return next.streamGeneration > previous.streamGeneration ? keyframeDecision(next.keyframe) : null;
}

function sameStreamFrameDecision(
  previous: BrowserFrameRecoveryState,
  next: BrowserFrameRecoveryInput,
): BrowserFrameDecision | null {
  if (!previous.streamId || !next.streamId) return null;
  if (previous.streamId !== next.streamId) return keyframeDecision(next.keyframe);
  if (next.sequence <= previous.observedSequence) return REJECT_BROWSER_FRAME;
  const missingFrameCount = Math.max(0, next.sequence - previous.observedSequence - 1);
  return {
    accept: missingFrameCount === 0 || next.keyframe === true,
    observe: true,
    missingFrameCount,
    startsNewStream: false,
  };
}

function legacyFrameDecision(
  previous: BrowserFrameRecoveryState,
  next: BrowserFrameRecoveryInput,
): BrowserFrameDecision {
  if (next.sequence > previous.acceptedSequence) {
    return { accept: true, observe: true, missingFrameCount: 0, startsNewStream: false };
  }
  if (!previous.acceptedCapturedAt) return REJECT_BROWSER_FRAME;
  const previousTime = Date.parse(previous.acceptedCapturedAt);
  const nextTime = Date.parse(next.capturedAt);
  if (Number.isNaN(previousTime) || Number.isNaN(nextTime) || nextTime <= previousTime) {
    return REJECT_BROWSER_FRAME;
  }
  return { accept: true, observe: true, missingFrameCount: 0, startsNewStream: false };
}

function browserFrameDecision(
  previous: BrowserFrameRecoveryState,
  next: BrowserFrameRecoveryInput,
): BrowserFrameDecision {
  return (
    initialVersionedFrameDecision(previous, next) ??
    frameGenerationDecision(previous, next) ??
    sameStreamFrameDecision(previous, next) ??
    legacyFrameDecision(previous, next)
  );
}

export function resolveBrowserFrameRecovery(
  previous: BrowserFrameRecoveryState,
  next: BrowserFrameRecoveryInput,
): BrowserFrameRecoveryResult {
  const decision = browserFrameDecision(previous, next);
  if (!decision.observe) {
    return {
      accept: decision.accept,
      observe: decision.observe,
      missingFrameCount: decision.missingFrameCount,
      state: previous,
    };
  }
  const observedSequence = decision.startsNewStream
    ? next.sequence
    : Math.max(previous.observedSequence, next.sequence);
  const observedState: BrowserFrameRecoveryState = {
    ...previous,
    observedSequence,
    gapCount: previous.gapCount + decision.missingFrameCount,
  };
  const state = decision.accept
    ? {
        ...observedState,
        acceptedSequence: next.sequence,
        acceptedCapturedAt: next.capturedAt,
        ...(next.streamId ? { streamId: next.streamId } : {}),
        ...(next.streamGeneration !== undefined ? { streamGeneration: next.streamGeneration } : {}),
      }
    : observedState;
  return {
    accept: decision.accept,
    observe: decision.observe,
    missingFrameCount: decision.missingFrameCount,
    state,
  };
}
